// src/pages/UserProfilePage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { normalizeRole, ROLE_VALUE } from "../constants/roleLabels";
import { calculateHelperReputation } from "../services/reputationService";
import { logger } from "../utils/logger";
import { MapPin, ArrowLeft } from "lucide-react";

const socialLabel = {
  github: "GitHub",
  linkedin: "LinkedIn",
  website: "Website",
};

const buildSocialLinks = (social = {}) => {
  return [
    { key: "github", label: socialLabel.github, value: social.github },
    { key: "linkedin", label: socialLabel.linkedin, value: social.linkedin },
    { key: "website", label: socialLabel.website, value: social.website },
  ].filter((item) => Boolean(item.value && String(item.value).trim()));
};

function UserProfilePage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { currentUser, currentUserRole } = useAuth();
  const [user, setUser] = useState(null);
  const [helperProfile, setHelperProfile] = useState(null);
  const [stats, setStats] = useState({
    problemsSolved: 0,
    problemsCreated: 0,
    rating: 0,
    reviewsCount: 0,
  });
  const [reputation, setReputation] = useState(null);
  const [loadingReputation, setLoadingReputation] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Fetch user document
        const userDoc = await getDoc(doc(db, "users", uid));
        if (!userDoc.exists()) {
          setUser(null);
          setLoading(false);
          return;
        }

        const userData = { id: userDoc.id, ...userDoc.data() };
        setUser(userData);

        // If helper, load helper profile (includes advanced details/certifications)
        if (userData.role === "helper") {
          try {
            const helperDoc = await getDoc(doc(db, "helpers", uid));
            if (helperDoc.exists()) {
              setHelperProfile(helperDoc.data());
            } else {
              setHelperProfile(null);
            }
          } catch (helperErr) {
            console.error("Error fetching helper profile:", helperErr);
            setHelperProfile(null);
          }
        } else {
          setHelperProfile(null);
        }

        // Fetch stats (problems created)
        const problemsCreatedQuery = query(
          collection(db, "problems"),
          where("clientId", "==", uid)
        );
        const problemsCreatedSnap = await getDocs(problemsCreatedQuery);
        const problemsCreated = problemsCreatedSnap.size;

        // Fetch stats (problems solved)
        const problemsSolvedQuery = query(
          collection(db, "problems"),
          where("helperId", "==", uid),
          where("status", "==", "completed")
        );
        const problemsSolvedSnap = await getDocs(problemsSolvedQuery);
        const problemsSolved = problemsSolvedSnap.size;

        setStats({
          problemsCreated,
          problemsSolved,
          rating: userData.rating || 0,
          reviewsCount: userData.reviewsCount || 0,
        });

        // Fetch reputation metrics if user is a helper
        if (userData.role === 'helper') {
          setLoadingReputation(true);
          try {
            logger.log('📊 Fetching reputation for helper:', uid);
            const reputationMetrics = await calculateHelperReputation(uid);
            logger.log('📊 Reputation metrics received:', reputationMetrics);
            setReputation(reputationMetrics);
          } catch (error) {
            console.error('❌ Error fetching reputation:', error);
            // Set empty reputation so section still shows
            setReputation({
              completionRate: 0,
              completionRateDisplay: 'No data',
              completionDetails: 'No tasks yet',
              avgResponseTime: null,
              avgResponseTimeDisplay: null,
              jobsCompletedInLast7Days: 0,
              recentActivityDisplay: null,
              totalJobs: 0,
              completedJobs: 0,
            });
          } finally {
            setLoadingReputation(false);
          }
        } else {
          setLoadingReputation(false);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [uid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">404</h1>
          <p className="text-slate-600 mb-4">User not found</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const isOwnProfile = currentUser?.uid === uid;
  const socialLinks = buildSocialLinks(user?.social);
  const advancedDetails = helperProfile?.advancedDetails || {};
  const certifications = Array.isArray(advancedDetails.certifications)
    ? advancedDetails.certifications.filter((c) => Boolean(c && String(c).trim()))
    : [];

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4 sm:space-x-6 flex-1 min-w-0">
              {/* Avatar */}
              <div className="w-16 h-16 sm:w-24 sm:h-24 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl sm:text-3xl font-bold flex-shrink-0">
                {getInitials(user.name || user.displayName || user.email)}
              </div>

              {/* User Info */}
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2 truncate">
                  {user.name || user.displayName || "Anonymous User"}
                </h1>
                <p className="text-sm sm:text-base text-slate-600 mb-1 sm:mb-2 truncate">{user.email}</p>
                {user.bio && (
                  <p className="text-xs sm:text-sm text-slate-700 mt-2 max-w-2xl line-clamp-2 sm:line-clamp-none">{user.bio}</p>
                )}
                {/* Location Display */}
                {user.location && (
                  <p className="text-xs sm:text-sm text-slate-600 mt-1 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                    <span>
                      {typeof user.location === 'object' 
                        ? `${user.location.city || ''}${user.location.city && user.location.country ? ', ' : ''}${user.location.country || ''}`.trim() || 'Location not specified'
                        : user.location || 'Location not specified'}
                    </span>
                  </p>
                )}
                {isOwnProfile && (
                  <span className="inline-block mt-2 bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
                    Your Profile
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-2 w-full sm:w-auto">
              {isOwnProfile && currentUserRole && (
                <button
                  onClick={() =>
                    navigate(
                      normalizeRole(currentUserRole) === ROLE_VALUE.specialist
                        ? '/dashboard-helper'
                        : '/dashboard-client'
                    )
                  }
                  className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm sm:text-base flex-1 sm:flex-none"
                >
                  Dashboard
                </button>
              )}
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-3 sm:px-4 py-2 text-slate-700 hover:bg-slate-50 transition text-sm sm:text-base flex-1 sm:flex-none"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            </div>
          </div>

          {/* Social Links */}
          {socialLinks.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.value}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2 border rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition text-sm font-medium truncate"
                  title={link.value}
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}

          {/* Role Badge */}
          <div className="mt-4 sm:mt-6">
            <span className="inline-block bg-blue-100 text-blue-800 px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold capitalize">
              {user.role || "User"}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1 sm:mb-2">
              {stats.problemsCreated}
            </div>
            <div className="text-xs sm:text-sm text-slate-600">Problems Created</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-emerald-600 mb-1 sm:mb-2">
              {stats.problemsSolved}
            </div>
            <div className="text-xs sm:text-sm text-slate-600">Problems Solved</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-amber-600 mb-1 sm:mb-2">
              {stats.rating > 0 ? stats.rating.toFixed(1) : "N/A"}
            </div>
            <div className="text-xs sm:text-sm text-slate-600">Average Rating</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1 sm:mb-2">
              {stats.reviewsCount}
            </div>
            <div className="text-xs sm:text-sm text-slate-600">Reviews</div>
          </div>
        </div>

        {/* Reputation Metrics (for helpers) */}
        {user.role === 'helper' && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">
              Reputation Metrics
            </h2>
            {loadingReputation ? (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm">Loading reputation metrics...</p>
              </div>
            ) : reputation ? (
              <div className="space-y-4">
                {/* Completion Rate */}
                {reputation.totalJobs > 0 ? (
                  <div className="border-b border-slate-200 pb-3 last:border-b-0 last:pb-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">Completion Rate</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {reputation.completionRate}%
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">{reputation.completionRateDisplay}</p>
                    <p className="text-xs text-slate-500 mt-1">{reputation.completionDetails}</p>
                  </div>
                ) : (
                  <div className="border-b border-slate-200 pb-3 last:border-b-0 last:pb-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">Completion Rate</span>
                      <span className="text-lg font-bold text-slate-400">N/A</span>
                    </div>
                    <p className="text-xs text-slate-500">No jobs yet</p>
                  </div>
                )}

              {/* Average Response Time */}
              {reputation.avgResponseTimeDisplay && (
                <div className="border-b border-slate-200 pb-3 last:border-b-0 last:pb-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">Average Response Time</span>
                    <span className="text-lg font-bold text-blue-600">
                      {reputation.avgResponseTimeDisplay.value} {reputation.avgResponseTimeDisplay.unit}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600">{reputation.avgResponseTimeDisplay.full}</p>
                </div>
              )}

                {/* Recent Activity */}
                {reputation.recentActivityDisplay ? (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">Recent Activity</span>
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">
                        Active
                      </span>
                    </div>
                    <p className="text-xs text-slate-600">{reputation.recentActivityDisplay}</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">Recent Activity</span>
                      <span className="text-slate-400 text-xs">No recent activity</span>
                    </div>
                    <p className="text-xs text-slate-500">No jobs completed in the last 7 days</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-slate-500 text-sm">Loading reputation metrics...</p>
              </div>
            )}
          </div>
        )}

        {/* Additional Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4">
            About {user.name || user.displayName || "this user"}
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Member Since:</span>
              <span className="font-semibold text-slate-900">
                {user.createdAt
                  ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Role:</span>
              <span className="font-semibold text-slate-900 capitalize">
                {user.role || "User"}
              </span>
            </div>
            {/* Location Info */}
            {user.location && (
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Location:</span>
                <span className="font-semibold text-slate-900">
                  {typeof user.location === 'object'
                    ? `${user.location.city || ''}${user.location.city && user.location.country ? ', ' : ''}${user.location.country || ''}`.trim() || 'Not specified'
                    : user.location || 'Not specified'}
                </span>
              </div>
            )}
            {user.skills && user.skills.length > 0 && (
              <div className="pt-4 border-t">
                <span className="text-slate-600 block mb-2">Skills:</span>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {user.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Advanced details (helpers) */}
            {helperProfile && (advancedDetails.extendedBio || advancedDetails.hourlyRate || certifications.length > 0) && (
              <div className="pt-4 border-t space-y-3">
                <span className="text-slate-600 block">Advanced Details:</span>
                {advancedDetails.extendedBio && (
                  <p className="text-sm text-slate-700 whitespace-pre-line">{advancedDetails.extendedBio}</p>
                )}
                {advancedDetails.hourlyRate && (
                  <p className="text-sm text-slate-700">
                    <span className="font-semibold">Hourly Rate:</span> {advancedDetails.hourlyRate}
                  </p>
                )}
                {certifications.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Certifications:</p>
                    <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
                      {certifications.map((cert, idx) => (
                        <li key={idx}>{cert}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Future: Reviews section, activity feed, etc. */}
      </div>
    </div>
  );
}

export default UserProfilePage;

